import React, { useState } from 'react'
import MainContainer from '../UI/MainContainer'
import { ApplicationState } from '../Store/intialState'
import SyncIcon from '@material-ui/icons/Sync'
import SyncProblemIcon from '@material-ui/icons/SyncProblem'
import * as Yup from 'yup'
import LargeModal from 'UI/LargeModal'
import { Formik, ErrorMessage } from 'formik'
import useShallowEqualSelector from 'hooks/global'
import FormInputGroup from 'UI/FormInputGroup'
import { TextField } from '@duik/it'
import { CompanyProcoreInfo } from 'Companies/Types/types'
import { useDispatch } from 'react-redux'
import { editCompany, getToken } from 'Companies/Store/actions'
import SmBannerModal from 'UI/SmBannerModal'
import {
  InnerContainner,
  TextWrapper,
  SubTitle,
  StyledForm,
  FirstField,
  ErrorStyle,
  Text
} from './Styles/procoreSettings'
import { ProcoreSettingsProps } from './Types/types'

const ProcoreSettings: React.FC<ProcoreSettingsProps> = ({ setProcoreSettings }) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const dispatch = useDispatch()

  const { company, loading, error, userData } = useShallowEqualSelector(
    (state: ApplicationState) => ({
      company: state.companies?.company,
      loading: state.companies.loading,
      error: state.companies.error,
      userData: state.user.userData
    })
  )

  const saveCompanyValues = (values: CompanyProcoreInfo) => {
    const companyValues = JSON.parse(JSON.stringify(values))
    const companyInput = {
      procore: { ...company.procore, ...companyValues }
    }
    dispatch(editCompany(userData.companyId, companyInput))
    setProcoreSettings(false)
  }

  const testConnection = async (clientId: string | undefined, clientSecret: string | undefined) => {
    if (!clientId || !clientSecret) {
      return
    }

    setModalVisible(true)
    dispatch(getToken(clientId, clientSecret))
  }

  return (
    <MainContainer>
      <Formik
        initialValues={{
          clientId: company?.procore?.clientId || '',
          clientSecret: company?.procore?.clientSecret || ''
        }}
        validateOnChange
        validationSchema={ProcoreSchema}
        onSubmit={values => {
          if (values) {
            saveCompanyValues(values)
          }
        }}
      >
        {({ handleChange, handleSubmit, values }) => (
          <LargeModal
            handleLinkClick={() => testConnection(values?.clientId, values?.clientSecret)}
            linkClickDisabled={!company.procore?.clientId || !company.procore?.clientSecret}
            handleSaveButton={handleSubmit}
            title="Procore Settings"
            saveButtonText="Ok"
            saveButtonDisabled={false}
            linkText="Test Connection"
          >
            {modalVisible && (
              <SmBannerModal
                heading={!loading && error ? 'Testing Connection - Error' : 'Testing Connection'}
                text={
                  loading
                    ? 'Connecting to Procore…'
                    : !error
                    ? 'Connected successfully with your Client ID and Secret'
                    : 'Could not connect with your Client ID and Secret'
                }
                icon={!loading && error ? <SyncProblemIcon /> : <SyncIcon />}
                linkText={loading ? 'Cancel' : undefined}
                buttonText={!loading ? 'Ok' : undefined}
                onButtonClick={() => setModalVisible(false)}
                onLinkClick={() => setModalVisible(false)}
              />
            )}
            <InnerContainner>
              <TextWrapper>
                <SubTitle>Account Details</SubTitle>
                <Text>Use this account to connect to Procore</Text>
              </TextWrapper>
              <StyledForm>
                <FormInputGroup direction="horizontal">
                  <FirstField>
                    <TextField
                      name="clientId"
                      className="fullWidthInputBox"
                      label="PROCORE CLIENT ID"
                      onChange={handleChange}
                      value={values?.clientId}
                    />
                    <ErrorMessage name="clientId" render={msg => <ErrorStyle>{msg}</ErrorStyle>} />
                  </FirstField>
                  <div>
                    <TextField
                      name="clientSecret"
                      className="fullWidthInputBox"
                      label="PROCORE CLIENT SECRET"
                      onChange={handleChange}
                      value={values?.clientSecret}
                    />
                    <ErrorMessage
                      name="clientSecret"
                      render={msg => <ErrorStyle>{msg}</ErrorStyle>}
                    />
                  </div>
                </FormInputGroup>
              </StyledForm>
            </InnerContainner>
          </LargeModal>
        )}
      </Formik>
    </MainContainer>
  )
}

const ProcoreSchema = Yup.object().shape({
  clientId: Yup.string().required('Required'),
  clientSecret: Yup.string().required('Required')
})

export default ProcoreSettings
